import { ArrowDropDown, PlayCircle } from "@mui/icons-material";
import { Button, Menu, MenuItem } from "@mui/material";
import { Box, SxProps, Theme } from "@mui/system";
import { useEffect, useState } from "react";
import { Process, ProcessExecution } from "../../../../model/Process";

interface ProcessQuickDropdownProps {
  processes: Array<Process>;
  handleStart: (process: Process) => void;
  sx?: SxProps<Theme>;
  lastProcessExecution?: ProcessExecution;
}
export const ProcessQuickDropdown = ({
  processes,
  handleStart,
  ...props
}: ProcessQuickDropdownProps) => {
  // Preferences
  const autoLaunchProcess = true; // If true, launch process automatically when selected from menu

  // Menu Props
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchor);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor(event.currentTarget);
  };
  const menuProps = processes.map((p) => (
    <MenuItem key={p.meta} onClick={() => handleSelect(p)}>
      {p.name}
    </MenuItem>
  ));
  const handleClose = () => {
    setAnchor(null);
  };
  const handleSelect = (process: Process) => {
    if (autoLaunchProcess) {
      handleStart(process);
      return;
    }
    setSelectedProcess(process);
    handleClose();
  };

  // Button Props
  const [selectedProcess, setSelectedProcess] = useState<Process | undefined>(
    undefined,
  );
  const startProcess = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (selectedProcess) {
      handleStart(selectedProcess);
    } else {
      setAnchor(event.currentTarget);
    }
  };
  useEffect(() => {
    if (props.lastProcessExecution) {
      const process = processes.find((process) => {
        return process.meta == props.lastProcessExecution?.process_id;
      });

      if (process) {
        setSelectedProcess(process);
      }
    } else {
      setSelectedProcess(undefined);
    }
  }, [processes, props.lastProcessExecution]);

  // Show full dropdown if there are previous process executions
  if (selectedProcess) {
    return (
      <Box sx={{ whiteSpace: "nowrap", ...props.sx, display: "inline-flex" }}>
        <Button
          onClick={startProcess}
          startIcon={<PlayCircle />}
          variant="contained"
          color="primary"
          disableElevation
          sx={(theme) => ({
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            height: "32px",
            minWidth: 120,
            borderRight: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
            backgroundColor: theme.palette.primary.main,
          })}
        >
          {selectedProcess?.name || "Select Flight Plan"}
        </Button>
        <Button
          id="process-dropdown-button"
          onClick={handleClick}
          aria-controls={isOpen ? "process-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? "true" : undefined}
          variant="contained"
          color="primary"
          disableElevation
          sx={(theme) => ({
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
            height: "32px",
            minWidth: 36,
            px: 0.5,
            boxShadow: "none",
            backgroundColor: theme.palette.primary.main,
          })}
        >
          <ArrowDropDown sx={{ mt: "0.5px" }} />
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchor}
          open={isOpen}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "process-dropdown-button",
          }}
        >
          {menuProps}
        </Menu>
      </Box>
    );
  } else {
    // Otherwise show limited dropdown.
    return (
      <Box sx={{ whiteSpace: "nowrap", ...props.sx, display: "inline-flex" }}>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={handleClick}
          endIcon={<ArrowDropDown />}
          sx={(theme) => ({
            borderRadius: 16,
            height: "32px",
            boxShadow: "none",
            backgroundColor: theme.palette.primary.main,
          })}
        >
          {"Start a Flight Plan"}
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchor}
          open={isOpen}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "process-dropdown-button",
          }}
        >
          {menuProps}
        </Menu>
      </Box>
    );
  }
};
